import React, {useContext, useEffect, useState} from "react";
import {Grid, Typography, Divider, Stack} from "@leap4/frontend";
import {AdServerTagSideEnum} from "@leap4/entities-discrepancy-common";
import {metricEnumKeyMap} from "../../../../../utils/enums";
import {ComparisonState} from "../../../../../config/generals";
import {extractOverviewMetrics, getMetricChangePercent} from "../../../common/OverviewGeneralInfo/OverviewHelpers";
import {IAdServerTagOverview} from "../interfaces";
import {SingleMetric} from "../../../common/OverviewGeneralInfo/SingleMetric";
import {urlService} from "../../../../routing/urlsService";
import {Link} from "react-router-dom";
import {UserViewsContext} from "../../../../../contexts/userViewsContext";

export interface ITagGeneralInfoProps {
    data: IAdServerTagOverview
    comparisonData: ComparisonState
}

// Component for displaying just the tag information (name, datapoint, campaign, partner, etc.)
const TagInfo = ({data}: {data: IAdServerTagOverview}): JSX.Element => {
    const {tag, partner} = data;
    const {dataPoint, name, campaignId, side, companyId} = tag;
    const {name: dataPointName} = dataPoint;

    return (
        <Grid container direction={"column"} alignItems={"center"} justifyContent={"center"} style={{padding: '15px', height: '100%'}}>
            <Grid item xs={12} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Stack 
                    direction={{ xs: 'column', md: 'row' }} 
                    spacing={2} 
                    alignItems="center"
                    justifyContent="center"
                    flexWrap="wrap"
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
                >
                    <Typography variant="h6" fontWeight="bold">{name}</Typography>
                    <Typography variant="h6">{dataPointName}</Typography>
                    <Typography variant="h6">Campaign Id: {campaignId}</Typography>
                    <Typography variant="h6">
                        Partner: {partner ?
                            <Link to={urlService.editPartner(partner?.id)}><strong>{partner?.name}</strong></Link> 
                            : 'No Partner'}
                    </Typography>
                    <Typography variant="h6">
                        {`${side === AdServerTagSideEnum.SUPPLY ? "Publisher" : "Advertiser"}: ${companyId ? companyId : "unknown"}`}
                    </Typography>
                </Stack>
            </Grid>
        </Grid>
    )
}

// Component for displaying the metrics
const TagMetrics = ({data, comparisonData}: ITagGeneralInfoProps): JSX.Element => {
    const {userViews} = useContext(UserViewsContext)
    const [initialAllMetrics] = useState(extractOverviewMetrics(data.selectedDates[0].totals))
    const [selectedDatesMetrics, setSelectedMetrics] = useState(extractOverviewMetrics(data.selectedDates[0].totals))
    const [selectedComparisonMetrics, setSelectedComparisonMetrics] = useState(extractOverviewMetrics(comparisonData.data))
    const constantGap = '10px'

    useEffect(() => {
        const {overviewViews} = userViews.activePreset
        const newSelectedMetrics = {}
        overviewViews.forEach(view => {
            newSelectedMetrics[view] = initialAllMetrics[view]
        })
        setSelectedMetrics(newSelectedMetrics as any)
        setSelectedComparisonMetrics(extractOverviewMetrics(comparisonData.data))
    }, [userViews, comparisonData])

    return (
        <Grid container alignItems={"center"} justifyContent={"center"} style={{padding: '15px', height: '100%'}}>
            <Grid item width={"100%"}>
                <Grid container gap={3} width={"100%"} padding={"0 15px"} height={"100%"}
                      justifyContent={"space-around"} flexWrap={"wrap"}>
                    {Object.entries(selectedDatesMetrics).map(([metricKey, metricValue]) => (
                            <Grid item key={metricKey}>
                                <SingleMetric
                                    metricChange={getMetricChangePercent(metricValue, selectedComparisonMetrics[metricKey])}
                                    metricValue={metricValue} metricEnum={metricEnumKeyMap[metricKey].label}/>
                            </Grid>
                        )
                    )}
                </Grid>
            </Grid>
        </Grid>
    )
}

// Combined component (for backward compatibility)
const TagGeneralInfo = ({data, comparisonData}: ITagGeneralInfoProps): JSX.Element => {
    return (
        <Grid container direction={"column"} style={{height: '100%'}}>
            <TagInfo data={data} />
            <TagMetrics data={data} comparisonData={comparisonData} />
        </Grid>
    )
}

export {TagGeneralInfo, TagInfo, TagMetrics}

